import React, { useState } from 'react'
import { useLazyQuery, DocumentNode } from '@apollo/client'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Box } from '@mui/material'
import { Button } from '@center/ui-kit'
import Modal from 'components/Modal'

import { downloadBase64 } from 'utils'

interface ExportFormat {
  type: string
  icon: string
  label: string
}

interface ExportData {
  [key: string]: string
}

interface ExportProps {
  title: string
  formats: ExportFormat[]
  query: DocumentNode
  variables?: Record<string, unknown>
  exportFieldName: string
  onExport?: (format: string) => void
}

const Export = ({ title, formats, query, variables = {}, exportFieldName, onExport }: ExportProps) => {
  const [formatType, setFormatType] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [launchExport, { error }] = useLazyQuery<ExportData>(query)
  const { t } = useTranslation()
  const navigate = useNavigate()

  const handleExport = async (format: string) => {
    try {
      setLoading(true)
      const result = await launchExport({
        variables: {
          format,
          ...variables
        }
      })

      if (result?.data?.[exportFieldName]) {
        const base64 = result.data[exportFieldName]
        await downloadBase64({ base64, fileName: `users_${moment().format('YYYY/MM/DD HH:mm')}.${format}`})
        onExport?.(format)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      icon={<i className="fa-solid fa-arrow-up-from-line" />}
      title={t(title)}
      onClose={() => navigate('..')}
    >
      <Box display="flex" alignSelf="center" mt={3} gap={8}>
        {error ? (
          <Box sx={{ fontSize: '14px' }}>{t('export.error')}</Box>
        ) : (
          <>
            {formats.map(format => (
              <Button
                key={format.type}
                startIcon={<i className={`fa-light ${format.icon}`} />}
                size="large"
                onClick={async () => {
                  setFormatType(format.type)
                  await handleExport(format.type)
                }}
                disabled={loading && formatType === format.type}>
                {t(format.label)}
              </Button>
            ))}
          </>
        )}
      </Box>
    </Modal>
  )
}

export default Export
